/**
 * GestureChannel — one-shot body-gesture layer.
 *
 * Plays a single body clip on top of whatever the blueprint graph is driving,
 * with a short fade-in, hold, and fade-out. Used to layer gestures like
 * "lean in", "laugh", "sigh" onto the current SM state (idle / listening /
 * responding) while LiveLink still drives the face.
 *
 * Lifecycle per gesture:
 *   t = 0 ............... play() called, start = now
 *   0 .. fadeInMs ....... weight ramps 0 → peakWeight
 *   fadeInMs .. fadeOutFrom .. weight held at peakWeight
 *   fadeOutFrom .. totalMs .. weight ramps peakWeight → 0
 *   t > totalMs ......... gesture done; its player stops and returns to the pool
 *
 * Each gesture plays on ITS OWN player (animPlayerPool.js), not the clip's
 * template: a gesture fired while another is playing — the same one included —
 * starts a fresh copy from frame 0, and the one it replaces fades out from the
 * weight it had over its own fade-out time, instead of being cut dead (a
 * different clip) or carrying on mid-way (the same clip, when both shared one
 * action). The channel is still single-slot: `active` is the newest gesture,
 * and only it can be stopped, queried or replaced; the ones fading out simply
 * finish.
 *
 * `apply(actions)` is called by GraphEvaluator.applyWeights every frame,
 * AFTER the main weight loop, so the gesture weight is not overwritten by
 * the graph's per-frame weight reset.
 */

import { getBodyClipLoader } from './bodyClipLoaderRef.js'
import { playerPoolFor } from './animPlayerPool.js'

const DEFAULT_PEAK_WEIGHT = 0.7
const DEFAULT_FADE_MS     = 250

// Live state — module-scoped. `_active` is the newest gesture; a new play()
// call moves it to `_fading`, where it fades out on its own player.
let _active = null
const _fading = []

export function playBodyGesture(clipName, {
  durationMs = 3000,
  peakWeight = DEFAULT_PEAK_WEIGHT,
  fadeInMs   = DEFAULT_FADE_MS,
  fadeOutMs  = DEFAULT_FADE_MS,
} = {}) {
  if (!clipName) return
  // A conversational override (BodyMotion locomotion, or a performance-
  // classified generated clip — both routed through playGeneratedClip with
  // followAction: true) already owns the skeleton exclusively; applyWeights
  // zeroes any additive gesture weight while it's active anyway, but starting
  // a NEW envelope here would leave it primed to pop back in for the tail of
  // its fade-out once the override releases. Scoped to followAction so
  // dev/test direct-plays (prefab Test button, Clips preview) — which never
  // set it — are unaffected.
  if (typeof window !== 'undefined' && window.__DIRECT_PLAY__?.followAction) return
  // JIT: gesture metadata arrives 1–3s before the audio, and gesture clips load
  // in the background (not in the priority set). Pull this clip to the front of
  // the load queue now so it's registered by the time the envelope ramps up.
  getBodyClipLoader()?.request(clipName)
  const now = performance.now()
  if (_active) fadeOut(_active, now)
  _active = {
    clipName,
    startTime:   now,
    fadeInUntil: now + fadeInMs,
    fadeOutFrom: now + Math.max(fadeInMs, durationMs - fadeOutMs),
    endTime:     now + durationMs,
    peakWeight,
    fadeOutMs,
    fadeFrom:    peakWeight,   // the weight the fade-out starts from
    player:      null,         // owned by applyGesture
  }
}

/** Hand `g` to the fading list, fading from the weight it has NOW. */
function fadeOut(g, now) {
  const env = envelopeOf(g, now)
  if (env.done || !g.player) {
    // Never applied (or already over): nothing on screen to fade.
    release(g)
    return
  }
  g.fadeFrom    = env.weight
  g.fadeOutFrom = now
  g.endTime     = now + g.fadeOutMs
  g.fadeInUntil = now   // no fade-in left
  _fading.push(g)
}

function release(g) {
  if (g.player) g.player.pool.release(g.player)
  g.player = null
}

export function stopBodyGesture() {
  if (_active) release(_active)
  _active = null
  for (const g of _fading) release(g)
  _fading.length = 0
}

export function isActive() {
  return _active !== null
}

function envelopeOf(a, now) {
  if (now >= a.endTime) return { clipName: a.clipName, weight: 0, done: true }
  if (now < a.fadeInUntil) {
    return { clipName: a.clipName, weight: a.peakWeight * ((now - a.startTime) / (a.fadeInUntil - a.startTime)), done: false }
  }
  if (now < a.fadeOutFrom) return { clipName: a.clipName, weight: a.peakWeight, done: false }
  const t = (now - a.fadeOutFrom) / (a.endTime - a.fadeOutFrom)
  return { clipName: a.clipName, weight: a.fadeFrom * (1 - t), done: false }
}

/** The active gesture's clip and envelope weight now, or null when nothing plays.
 *
 *  Split out of applyGesture because the splat's part rig takes weights, not mixer
 *  actions. `done` is the final tick — the caller zeroes and calls stopBodyGesture.
 *  (The part rig plays the newest gesture only; gestures fading out under it are
 *  a mixer-path behaviour.) */
export function gestureEnvelope(now = performance.now()) {
  return _active ? envelopeOf(_active, now) : null
}

/** Drive one gesture's player for this frame. Returns false once it is over. */
function applyOne(g, actions, now) {
  const env = envelopeOf(g, now)
  const template = actions?.[g.clipName]
  if (!template) {
    // Mapped clip not in the mixer yet. Under JIT loading the gesture clip may
    // still be streaming in — pull it to the front of the queue and keep the
    // channel alive so it plays once it lands. Only give up if the gesture's
    // envelope has already elapsed (too late to be worth playing).
    if (env.done) { release(g); return false }
    getBodyClipLoader()?.request(g.clipName)
    return true
  }
  if (env.done) {
    release(g)
    return false
  }
  let p = g.player
  if (p && p.source !== template.getClip()) { release(g); p = null }  // clip re-registered
  const fresh = !p
  if (!p) p = g.player = playerPoolFor(template.getMixer()).acquire(template.getClip())

  // Gesture plays additively over the base state so the underlying SM clip
  // (idle / responding / …) still drives the skeleton's resting pose; the
  // gesture layers limb motion on top. LoopOnce so it plays through exactly
  // once over its duration.
  const action = p.action
  action.blendMode = 2501 /* THREE.AdditiveAnimationBlendMode */
  if (action.loop !== 2200) action.setLoop(2200 /* THREE.LoopOnce */, 1)
  action.clampWhenFinished = true
  action.setEffectiveWeight(env.weight)
  if (fresh || !action.isRunning()) {
    // A fresh player starts at frame 0. One that stopped (a direct-play hold
    // sweeps the mixer) resumes where it was, as the envelope does.
    if (fresh) action.reset()
    action.play()
  }
  return true
}

/**
 * Called every frame by applyWeights after the main weight loop.
 * Drives each gesture's own player to its envelope weight.
 */
export function applyGesture(actions) {
  const now = performance.now()
  let keep = 0
  for (let i = 0; i < _fading.length; i++) {
    if (applyOne(_fading[i], actions, now)) _fading[keep++] = _fading[i]
  }
  _fading.length = keep
  if (_active && !applyOne(_active, actions, now)) _active = null
}

/** Call `fn(action)` for every gesture player's action (Layered Bone Blend's layer pass). */
export function forEachGestureAction(fn) {
  if (_active?.player) fn(_active.player.action)
  for (const g of _fading) if (g.player) fn(g.player.action)
}

/** Set every gesture player's weight to 0 this frame (a direct-play hold owns the body). */
export function silenceGestures() {
  if (_active?.player) _active.player.action.setEffectiveWeight(0)
  for (const g of _fading) if (g.player) g.player.action.setEffectiveWeight(0)
}

// Dev / test hook — tests read the current channel state
if (typeof window !== 'undefined') {
  window.__GESTURE_CHANNEL__ = {
    get active() { return _active },
    /** The newest gesture's own player (null until it is first applied). */
    get action() { return _active?.player?.action ?? null },
    /** Every gesture player on screen, newest last: `{ clipName, weight, action }`. */
    players() {
      const out = []
      for (const g of _fading) if (g.player) out.push({ clipName: g.clipName, weight: g.player.action.getEffectiveWeight(), action: g.player.action })
      if (_active?.player) out.push({ clipName: _active.clipName, weight: _active.player.action.getEffectiveWeight(), action: _active.player.action })
      return out
    },
    play:  playBodyGesture,
    stop:  stopBodyGesture,
  }
}
